import {
    autoUpdate,
    flip,
    offset,
    shift,
    useFloating,
} from "@floating-ui/react";
import { faXmark } from "@fortawesome/pro-light-svg-icons";
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover } from "@headlessui/react";
import { useMeasure } from "@uidotdev/usehooks";
import { PresetPill, useLocationPresets } from "../../DataTable/LocationColumn";

// Max width of each pill slot.
const PILL_SLOT_WIDTH = 130;

export const PresetIds = ({ presetIds }: { presetIds: number[] }) => {
    const presets = useLocationPresets(presetIds);
    const { refs, floatingStyles } = useFloating({
        strategy: "fixed",
        placement: "bottom-start",
        middleware: [flip(), offset({ mainAxis: 4, crossAxis: -5 }), shift()],
        whileElementsMounted: autoUpdate,
    });

    // Free space for pills = what the pills use + the empty space after them.
    // I'm not a big fan of this, but it works and its simple enough.
    const [pillsRef, pillsSize] = useMeasure();
    const [spacerRef, spacerSize] = useMeasure();
    const freeWidth = (pillsSize.width ?? 0) + (spacerSize.width ?? 0);
    const visibleCount = Math.max(1, Math.floor(freeWidth / PILL_SLOT_WIDTH));
    const hiddenCount = Math.max(0, presets.length - visibleCount);

    if (presets.length === 0) {
        return <span>No covering areas.</span>;
    }

    return (
        <div className="flex w-full min-w-0">
            <Popover as="div" ref={refs.setReference} className="flex min-w-0">
                <Popover.Button
                    title="Show all presets"
                    className="flex items-center gap-1 min-w-0 -ml-[5px] px-1 py-[3px] border border-transparent hover:border-[#d8dde8] hover:bg-white rounded-[14px]"
                >
                    <div ref={pillsRef} className="flex min-w-0">
                        {presets.slice(0, visibleCount).map((preset) => (
                            <span
                                key={preset.id}
                                className="flex min-w-0 pr-1"
                                style={{ maxWidth: PILL_SLOT_WIDTH }}
                            >
                                <PresetPill name={preset.name} />
                            </span>
                        ))}
                    </div>
                    {hiddenCount > 0 && (
                        <span className="flex-none h-6 px-2 rounded-full bg-[#e4f1f8] text-[#0f5a83] text-[12.5px] font-medium inline-flex items-center">
                            +{hiddenCount}
                        </span>
                    )}
                    <FontAwesomeIcon
                        icon={faChevronDown}
                        className="flex-none h-2.5 text-[#7b86a8]"
                    />
                </Popover.Button>
                <Popover.Panel
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="z-20 w-[280px] bg-white border border-[#dfe3ec] rounded-lg shadow-[0_10px_28px_rgba(29,43,94,0.16)] p-3 font-normal"
                >
                    {({ close }) => (
                        <>
                            <div className="flex items-center mb-2">
                                <span className="text-xs text-[#6b7699]">
                                    {presets.length} tag
                                    {presets.length === 1 ? "" : "s"}
                                </span>
                                <button
                                    type="button"
                                    title="Close"
                                    className="ml-auto text-[#6b7699] hover:text-[#1f2d6b]"
                                    onClick={() => close()}
                                >
                                    <FontAwesomeIcon
                                        icon={faXmark}
                                        className="h-3.5"
                                    />
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-[5px]">
                                {presets.map((preset) => (
                                    <PresetPill
                                        key={preset.id}
                                        name={preset.name}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </Popover.Panel>
            </Popover>
            {/* Empty space after the button, counted as free space */}
            <div ref={spacerRef} className="flex-1" />
        </div>
    );
};
